import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Crown, Zap, Sparkles, ShieldCheck } from "lucide-react";
import PlanCard from "@/components/billing/PlanCard";
import ExtraQuoteCard from "@/components/billing/ExtraQuoteCard";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import api from "@/lib/api";

export default function Pricing() {
  const cta = useSmartCTA();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  // No literal fallback: the price is platform_config's (PLANES, $3.00), and a
  // number typed here would be shown whenever the request failed.
  const [extraCreditPrice, setExtraCreditPrice] = useState(null);

  useEffect(() => {
    api.get("/billing/config")
      .then((r) => {
        setPlans(r.data.plans || []);
        setExtraCreditPrice(r.data.extra_credit_price_usd ?? null);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="pricing-page">
      <Navbar />

      {/* Hero */}
      <section className="section bg-honeycomb pb-12" data-testid="pricing-hero">
        <div className="container-app text-center">
          <span className="badge mb-6">
            <Sparkles size={12} className="text-hive-ink" /> {t("pricing.kicker")}
          </span>
          <h1 className="font-display text-5xl md:text-6xl font-extrabold leading-tight text-worker">
            {t("pricing.headline1")} <br />
            <span className="text-hive-ink">{t("pricing.headline2")}</span>
          </h1>
          <p className="mt-6 text-lg text-smoke max-w-2xl mx-auto">
            {t("pricing.subhead")}
          </p>
        </div>
      </section>

      {/* Plans grid — three identical cards (PLANES M4a) */}
      <section className="section pt-4" id="plans" data-testid="plans-section">
        <div className="container-app">
          <div className="grid md:grid-cols-3 gap-4 items-stretch">
            {plans.length === 0 ? (
              <div className="md:col-span-3 text-center text-smoke py-12">{t("common.loading")}</div>
            ) : plans.map((p) => (
              <PlanCard
                key={p.plan_id}
                plan={p}
                testId={`pricing-plan-${p.plan_id}`}
                footer={(
                  <Link
                    to={cta.planCheckout(p.tier)}
                    className="w-full fx-btn fx-btn--primary"
                    data-testid={`pricing-cta-${p.plan_id}`}
                  >
                    {p.tier === 0
                      ? (cta.isLoggedIn ? t("pricing.ctaDashboard") : t("pricing.ctaStartFree"))
                      : (cta.isLoggedIn ? t("pricing.ctaManage", { name: p.name }) : t("pricing.ctaStart", { name: p.name }))}
                    <ArrowRight size={16} />
                  </Link>
                )}
              />
            ))}
          </div>

          {/* An extra quote, its own card (PLANES M4b). Informational here: a
              visitor cannot buy one, so the way in is the billing page. */}
          {extraCreditPrice != null && (
            <div className="mt-6">
              <ExtraQuoteCard
                mode="info"
                price={extraCreditPrice}
                plans={plans}
                action={(
                  <Link to={cta.signup("provider")} className="fx-btn fx-btn--secondary text-sm" data-testid="pricing-credits-cta">
                    {cta.isLoggedIn ? t("pricing.credits.ctaBilling") : t("pricing.credits.ctaSignup")} <ArrowRight size={14} />
                  </Link>
                )}
              />
            </div>
          )}
        </div>
      </section>

      {/* FAQ-style trust strip */}
      <section className="section bg-linen" data-testid="trust-section">
        <div className="container-app">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-worker text-center mb-12">
            {t("pricing.trustStrip.title")}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { Icon: ShieldCheck, key: "c1" },
              { Icon: Zap, key: "c2" },
              { Icon: Crown, key: "c3" },
            ].map((card) => {
              const Icon = card.Icon;
              return (
                <div key={card.key} className="card-base p-6">
                  <Icon size={28} className="text-hive-ink" />
                  <h3 className="mt-3 font-display text-xl font-bold text-worker">{t(`pricing.trustStrip.${card.key}Title`)}</h3>
                  <p className="mt-2 text-smoke leading-relaxed">{t(`pricing.trustStrip.${card.key}Body`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section bg-hive" data-testid="final-cta-section">
        <div className="container-app text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-worker">
            {t("pricing.final.title")}
          </h2>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to={cta.becomeFixyBee()} className="fx-btn fx-btn--master" data-testid="final-cta-signup">
              {cta.isProvider ? t("pricing.ctaDashboard") : t("pricing.final.ctaSignup")} <ArrowRight size={18} />
            </Link>
            <Link to="/providers" className="fx-btn fx-btn--ghost text-worker hover:bg-worker/10" data-testid="final-cta-browse">
              {t("pricing.final.ctaBrowse")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
